import {ImportModulePart} from '../../core/definitions/import-module';

/**
 * Shared parsing for the Import module's `{Column}` value templates - a
 * plain string with `{Column}` placeholders that `POST /run` composes per
 * row. Used by both the flat assignment list and the group cards
 * so the two produce identical `parts`.
 */

/** One piece of a value template. */
export type TemplatePart = ImportModulePart;

export const PLACEHOLDER_RE = /\{([^{}]+)\}/g;

/**
 * Break a template into literal and `{Column}` parts. A `{name}` that is
 * not a known column stays literal (an operator typo shouldn't silently
 * become an empty value).
 */
export function parseTemplate(template: string, columnNames: ReadonlySet<string>): TemplatePart[] {
  const parts: TemplatePart[] = [];
  let lastIndex = 0;
  for (const match of template.matchAll(PLACEHOLDER_RE)) {
    if (match.index > lastIndex) {
      parts.push({kind: 'text', value: template.slice(lastIndex, match.index)});
    }
    parts.push(columnNames.has(match[1])
      ? {kind: 'column', value: match[1]}
      : {kind: 'text', value: match[0]});
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < template.length) {
    parts.push({kind: 'text', value: template.slice(lastIndex)});
  }
  return parts;
}

/** `{Placeholder}` names in the template that match no known column. */
export function unknownPlaceholders(template: string, columnNames: ReadonlySet<string>): string[] {
  const names = [...template.matchAll(PLACEHOLDER_RE)].map(m => m[1]);
  return [...new Set(names.filter(name => !columnNames.has(name)))];
}

/** One way to complete the template at the caret: `column` inserted as a
 * `{column}` placeholder. */
export interface ColumnCompletion {
  column: string;
  // The whole template once the placeholder is in.
  template: string;
  // Where the caret belongs afterwards - just past the inserted `}`.
  caret: number;
}

/**
 * The columns offered for completion with the caret at `caret`.
 *
 * Offered only where a placeholder is wanted: with the caret inside an
 * unclosed `{`, narrowed by what is typed after the brace, or in a
 * template that is still blank. Literal text - a prefix like
 * `"Fastighet: "` - therefore never triggers completion, and a template
 * with several columns completes each one from its own `{`.
 *
 * Columns whose name starts with the typed text come before those that
 * merely contain it; otherwise source order is kept. Completing inside an
 * existing `{...}` replaces the whole placeholder, not just the part
 * before the caret.
 */
export function columnCompletions(
  template: string, caret: number, columns: readonly string[]): ColumnCompletion[] {
  const before = template.slice(0, caret);
  const open = before.lastIndexOf('{');
  const insideBrace = open !== -1 && !before.slice(open).includes('}');
  if (!insideBrace && template.trim() !== '') {
    return [];
  }
  const start = insideBrace ? open : caret;
  const typed = insideBrace ? before.slice(open + 1).toLowerCase() : '';
  const after = template.slice(caret);
  const rest = insideBrace ? after.replace(/^[^{}]*\}/, '') : after;
  const matching = columns.filter(column => column.toLowerCase().includes(typed));
  const leading = matching.filter(column => column.toLowerCase().startsWith(typed));
  const containing = matching.filter(column => !column.toLowerCase().startsWith(typed));
  return [...leading, ...containing].map(column => ({
    column,
    template: `${template.slice(0, start)}{${column}}${rest}`,
    caret: start + column.length + 2,
  }));
}
